import { isUndefined } from 'lodash-es';
import { Children, type ReactElement, type ReactNode } from 'react';

import { Intent, IntentProps } from '../../../../core/slots/Intent/Intent.js';
import { isSlot } from '../../../../core/utils/is-slot.js';
import { ColorRule } from '../../../core/slots/ColorRule.js';
import { Toolbar } from '../../shared/slots/Toolbar.js';
import {
  handleColorRuleSlot,
  handleIntentSlot,
  handleLabelSlot,
  handleMaxSlot,
  handleMinSlot,
  handleToolbarSlot,
  handleValueSlot,
} from '../../shared/utils/slot-handlers.js';
import { Label } from '../slots/Label.js';
import { Legend } from '../slots/Legend.js';
import { Max } from '../slots/Max.js';
import { Min } from '../slots/Min.js';
import { Segment } from '../slots/Segment.js';
import { Tooltip } from '../slots/Tooltip.js';
import { Value } from '../slots/Value.js';
import { MultiMeterBarChartSlots } from '../types/multi-meter-bar-chart-config.js';
import {
  MultiMeterBarSegmentProps,
  type MultiMeterBarChartTooltipProps,
} from '../types/multi-meter-bar-chart.js';

function handleMultiMeterTooltipSlot(
  child: ReactElement<MultiMeterBarChartTooltipProps>,
  slots: MultiMeterBarChartSlots,
): void {
  const { variant, hidden, children } = child.props;
  slots.tooltip = { hidden: hidden ?? false, variant, template: children };
}

export function iterateSlots(children?: ReactNode) {
  const slots: MultiMeterBarChartSlots = {
    segments: [],
    colorRules: [],
    toolbar: { hidden: false },
  };

  let intents: IntentProps[] | undefined;
  Children.forEach(children, (child) => {
    if (isSlot(child, Label)) {
      handleLabelSlot(child, slots);
    } else if (isSlot(child, Value)) {
      handleValueSlot(child, slots);
    } else if (isSlot(child, Min)) {
      handleMinSlot(child, slots);
    } else if (isSlot(child, Max)) {
      handleMaxSlot(child, slots);
    } else if (isSlot(child, Segment)) {
      const { value, color, name, valueAccessor } = child.props;
      const segment = valueAccessor
        ? { value, color, name, valueAccessor }
        : { value, color, name };
      slots.segments = [
        ...slots.segments,
        segment as MultiMeterBarSegmentProps,
      ];
    } else if (isSlot(child, Legend)) {
      slots.legend = !(child.props.hidden ?? false);
    } else if (isSlot(child, Tooltip)) {
      handleMultiMeterTooltipSlot(child, slots);
    } else if (isSlot(child, ColorRule)) {
      handleColorRuleSlot(child, slots);
    } else if (isSlot(child, Intent)) {
      intents = handleIntentSlot(child, intents);
    } else if (isSlot(child, Toolbar)) {
      handleToolbarSlot(child, slots);
    }
  });

  if (!isUndefined(intents) && slots.toolbar) {
    slots.toolbar.intents = intents;
  }

  return slots;
}
